/**
 * NoteReviewPanel — PRP-NOTES-1. Chat writes customer notes as SUGGESTIONS and its working notes as
 * INTERNAL notes. Here the user picks this estimate's note level and adds, edits or leaves off each
 * suggestion. What the panel says about printing is what the export does (the server decides
 * `prints`; this component never recomputes it). Internal notes are shown read-only: they go to
 * Xactimate as internal notes and never print.
 *
 * Hidden when there is nothing to review on an imported estimate; always shown on a chat estimate
 * (id `mcp-…`) so the level can be set before chat writes anything.
 */
import { useEffect, useState } from 'react';

import {
  getNoteReview,
  patchNoteReview,
  type NoteLevel,
  type NoteReview,
  type NoteReviewPatch,
  type SuggestedLineNote,
  type SuggestedRoomNote,
} from '../../lib/noteReviewApi';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Pill } from './ui/pill';
import { Textarea } from './ui/textarea';

const SOURCE_LABEL: Record<NoteReview['note_level_source'], string> = {
  estimate: 'set on this estimate',
  account: 'your account default',
  default: 'the default',
};

type Flash = { kind: 'ok' | 'bad'; msg: string } | null;

function Status({ prints, review, held }: { prints: boolean; review: string | null; held: string | null }) {
  if (review === 'skipped') return <Pill kind="neutral">Left off</Pill>;
  if (prints) return <Pill kind="ok">{review === 'accepted' ? 'Added: prints' : 'Prints'}</Pill>;
  return (
    <span className="inline-flex items-center gap-2 flex-wrap">
      <Pill kind="warn">Not printed</Pill>
      {held && <span className="text-[11.5px] text-[var(--tx-lo)]">{held}</span>}
    </span>
  );
}

interface RowProps {
  title: string;
  text: string;
  reason: string | null;
  reasons: Record<string, string>;
  review: 'accepted' | 'skipped' | null;
  prints: boolean;
  held: string | null;
  internal?: string[];
  busy: boolean;
  testId: string;
  /** Resolves true when the server saved it (the editor closes only then; a failed save keeps the text). */
  onAction: (_action: 'accept' | 'skip' | 'reset' | 'edit', _text?: string) => Promise<boolean>;
}

function SuggestionRow({ title, text, reason, reasons, review, prints, held, internal, busy, testId, onAction }: RowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  return (
    <li className="py-3 border-t border-[var(--bd-subtle)] first:border-t-0" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 text-[12px] text-[var(--tx-lo)]">{title}</div>
        <Status prints={prints} review={review} held={held} />
      </div>
      {editing ? (
        <div className="mt-2 space-y-2">
          <Textarea
            aria-label={`Edit note: ${title}`}
            value={draft}
            maxLength={1000}
            rows={3}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="flex gap-2">
            <Button size="sm" variant="primary" disabled={busy || !draft.trim()}
              onClick={async () => { if (await onAction('edit', draft)) setEditing(false); }}>
              Save and add
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setDraft(text); setEditing(false); }}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <p className="m-0 mt-1 text-[13px] text-[var(--tx-hi)] whitespace-pre-wrap">{text}</p>
      )}
      {reason && (
        <p className="m-0 mt-1 text-[11.5px] text-[var(--tx-lo)]">Chat marked it unusual: {reasons[reason] ?? reason}</p>
      )}
      {internal && internal.length > 0 && (
        <p className="m-0 mt-1 text-[11.5px] text-[var(--tx-faint)] whitespace-pre-wrap" data-testid={`${testId}-internal`}>
          Internal note (never prints): {internal.join(' / ')}
        </p>
      )}
      {!editing && (
        <div className="mt-2 flex gap-2 flex-wrap">
          {review === null ? (
            <>
              <Button size="sm" variant="primary" disabled={busy} onClick={() => onAction('accept')}>Add</Button>
              <Button size="sm" disabled={busy} onClick={() => { setDraft(text); setEditing(true); }}>Edit</Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => onAction('skip')}>Leave off</Button>
            </>
          ) : (
            <>
              <Button size="sm" disabled={busy} onClick={() => { setDraft(text); setEditing(true); }}>Edit</Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => onAction('reset')}>Mark not reviewed</Button>
            </>
          )}
        </div>
      )}
    </li>
  );
}

/** A response without the review's lists is treated as a failed load, never rendered: a malformed or empty
 *  body (a proxy, an older API, a test double) must not take the whole estimate page down with it. */
function isReview(d: unknown): d is NoteReview {
  const r = d as Partial<NoteReview> | null;
  return !!r && Array.isArray(r.levels) && Array.isArray(r.lines) && Array.isArray(r.room_notes)
    && Array.isArray(r.room_internal_notes) && typeof r.note_level === 'string';
}

export default function NoteReviewPanel({ parseHash }: { parseHash: string }) {
  const [data, setData] = useState<NoteReview | null>(null);
  const [load, setLoad] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<Flash>(null);

  useEffect(() => {
    let cancelled = false;
    setLoad('loading');
    getNoteReview(parseHash)
      .then((d) => {
        if (cancelled) return;
        if (!isReview(d)) {
          setLoad('failed');
          return;
        }
        setData(d);
        setLoad('ready');
      })
      .catch(() => {
        if (!cancelled) setLoad('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [parseHash]);

  async function send(body: NoteReviewPatch, ok: string): Promise<boolean> {
    setBusy(true);
    setFlash(null);
    try {
      const next = await patchNoteReview(parseHash, body);
      if (!isReview(next)) throw new Error('malformed note review response');
      setData(next);
      setFlash({ kind: 'ok', msg: ok });
      return true;
    } catch (err: unknown) {
      const status = (err as { status?: number } | null)?.status;
      setFlash({
        kind: 'bad',
        msg: status === 409
          ? 'The estimate changed while you were reviewing. Reload the page and try again.'
          : 'Could not save. Nothing changed.',
      });
      return false;
    } finally {
      setBusy(false);
    }
  }

  const isChat = parseHash.startsWith('mcp-');
  if (load === 'loading') return null;
  if (load === 'failed' || !data) {
    return isChat ? (
      <Card className="px-4 py-[14px] mb-3" data-testid="note-review">
        <p className="m-0 text-[12.5px] text-[var(--bad)]">Could not load the notes to review. Try again later.</p>
      </Card>
    ) : null;
  }
  const empty = !data.lines.length && !data.room_notes.length && !data.room_internal_notes.length;
  if (empty && !isChat) return null;
  const waiting = data.lines.filter((l) => l.review === null).length + data.room_notes.filter((r) => r.review === null).length;

  const lineAction = (l: SuggestedLineNote) => (action: 'accept' | 'skip' | 'reset' | 'edit', text?: string) =>
    send({ lines: [{ room_index: l.room_index, line_item_index: l.line_item_index, line_number: l.line_number, action,
      ...(text ? { text } : {}) }] },
      action === 'skip' ? 'Left off. It will not print.' : action === 'reset' ? 'Marked not reviewed.' : 'Saved. It prints on the estimate.');
  const roomAction = (r: SuggestedRoomNote) => (action: 'accept' | 'skip' | 'reset' | 'edit', text?: string) =>
    send({ room_notes: [{ room_index: r.room_index, text: r.text, action, ...(text ? { new_text: text } : {}) }] },
      action === 'skip' ? 'Left off. It will not print.' : action === 'reset' ? 'Marked not reviewed.' : 'Saved. It prints on the estimate.');

  return (
    <Card className="px-4 py-[14px] mb-3" data-testid="note-review">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="m-0 text-[13px] font-semibold text-[var(--tx-hi)]">Notes for the customer</h3>
          <p className="m-0 mt-[2px] text-[11.5px] text-[var(--tx-lo)]">
            Notes chat suggested for the printed estimate. Add, edit or leave off each one.
            {waiting > 0 ? ` ${waiting} not reviewed.` : ''}
          </p>
        </div>
        {/* B-7627: at phone width the label takes its own row and the select shrinks to the card (no clipping). */}
        <label className="text-[12px] text-[var(--tx-lo)] flex items-center gap-2 min-w-0 w-full sm:w-auto" data-testid="note-level-label">
          <span className="shrink-0">Note level</span>
          <select
            data-testid="note-level"
            className="min-w-0 w-full sm:w-auto max-w-full truncate h-7 rounded-[var(--r-md)] bg-[var(--bg-inset)] border border-[var(--bd)] text-[var(--tx-hi)] text-[12.5px] px-2"
            value={data.note_level_source === 'estimate' ? data.note_level : 'account'}
            disabled={busy}
            onChange={(e) => send({ note_level: e.target.value as NoteLevel | 'account' }, 'Note level saved. The next export uses it.')}
          >
            <option value="account">Account default ({data.levels.find((l) => l.key === data.account_level)?.label.split(':')[0] ?? data.account_level})</option>
            {data.levels.map((l) => (
              <option key={l.key} value={l.key}>{l.label}</option>
            ))}
          </select>
        </label>
      </div>
      <p className="m-0 mt-2 text-[11.5px] text-[var(--tx-faint)]">
        In force: {data.levels.find((l) => l.key === data.note_level)?.label ?? data.note_level} ({SOURCE_LABEL[data.note_level_source]}).
        Notes you add always print. Notes that read like working notes (prices, to-dos, names) never print unless you add them.
      </p>
      {flash && (
        <p role="status" data-testid="note-review-flash"
          className={`m-0 mt-2 text-[12.5px] ${flash.kind === 'ok' ? 'text-[var(--ok)]' : 'text-[var(--bad)]'}`}>
          {flash.msg}
        </p>
      )}
      {empty ? (
        <p className="m-0 mt-3 text-[12.5px] text-[var(--tx-lo)]">No suggested notes yet.</p>
      ) : (
        <ul className="m-0 mt-2 p-0 list-none">
          {data.lines.map((l) =>
            l.suggested_note ? (
              <SuggestionRow
                key={`l-${l.room_index}-${l.line_item_index}-${l.suggested_note}`}
                testId={`note-line-${l.room_index}-${l.line_item_index}`}
                title={`${l.room} · line ${l.line_number} · ${l.code} ${l.description}`}
                text={l.suggested_note}
                reason={l.reason}
                reasons={data.reasons}
                review={l.review}
                prints={l.prints}
                held={l.held_reason}
                internal={l.internal_notes}
                busy={busy}
                onAction={lineAction(l)}
              />
            ) : (
              <li key={`i-${l.room_index}-${l.line_item_index}`} className="py-3 border-t border-[var(--bd-subtle)] first:border-t-0"
                data-testid={`note-line-${l.room_index}-${l.line_item_index}`}>
                <div className="text-[12px] text-[var(--tx-lo)]">{`${l.room} · line ${l.line_number} · ${l.code} ${l.description}`}</div>
                <p className="m-0 mt-1 text-[11.5px] text-[var(--tx-faint)] whitespace-pre-wrap">
                  Internal note (never prints): {l.internal_notes.join(' / ')}
                </p>
              </li>
            ),
          )}
          {data.room_notes.map((r) => (
            <SuggestionRow
              key={`r-${r.room_index}-${r.text}`}
              testId={`note-room-${r.room_index}`}
              title={`${r.room} · room note`}
              text={r.text}
              reason={r.reason}
              reasons={data.reasons}
              review={r.review}
              prints={r.prints}
              held={r.held_reason}
              busy={busy}
              onAction={roomAction(r)}
            />
          ))}
          {data.room_internal_notes.map((r) => (
            <li key={`ri-${r.room_index}`} className="py-3 border-t border-[var(--bd-subtle)] first:border-t-0">
              <div className="text-[12px] text-[var(--tx-lo)]">{r.room} · room</div>
              <p className="m-0 mt-1 text-[11.5px] text-[var(--tx-faint)] whitespace-pre-wrap">
                Internal note (never prints): {r.internal_notes.join(' / ')}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
